import { useCallback, useEffect, useState } from 'react';
import { Clock, Plus, Trash2, Loader2, Power, PowerOff } from 'lucide-react';
import { apiClient } from '../../lib/api-client';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';

interface AiSchedule {
  id: string;
  workflowName: string;
  schedule: string;
  enabled: boolean;
  createdBy: string;
  createdAt: string;
  lastRun?: string;
  parameters?: Record<string, any>;
}

interface AiScheduleDialogProps {
  open: boolean;
  streamId: string;
  streamDisplayName: string;
  /** When opened from a role row, schedules can tag `_aiRoleCode` for collect jobs. */
  roleCode?: string;
  roleLabel?: string;
  onClose: () => void;
}

const PRESETS: { label: string; cron: string }[] = [
  { label: 'Every hour', cron: '0 * * * *' },
  { label: 'Every 4 hours', cron: '0 */4 * * *' },
  { label: 'Every 8 hours', cron: '0 */8 * * *' },
  { label: 'Daily at 8am', cron: '0 8 * * *' },
  { label: 'Weekdays at 8am', cron: '0 8 * * 1-5' },
  { label: 'Weekly Monday 8am', cron: '0 8 * * 1' },
];

function humanizeCron(cron: string): string {
  const preset = PRESETS.find((p) => p.cron === cron);
  if (preset) return preset.label;
  return cron;
}

function utf8ToBase64(s: string): string {
  return btoa(unescape(encodeURIComponent(s)));
}

type JobKind = 'heartbeat' | 'collect_jira';

export function AiScheduleDialog({
  open,
  streamId,
  streamDisplayName,
  roleCode,
  roleLabel,
  onClose,
}: AiScheduleDialogProps) {
  const [schedules, setSchedules] = useState<AiSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newCron, setNewCron] = useState('0 */4 * * *');
  const [showCreate, setShowCreate] = useState(false);
  const [jobKind, setJobKind] = useState<JobKind>('heartbeat');
  const [collectJql, setCollectJql] = useState('updated >= -1d ORDER BY updated DESC');
  const [collectMax, setCollectMax] = useState('5');

  const load = useCallback(async () => {
    if (!open || !streamId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.getAiStreamSchedules(streamId);
      setSchedules(res.schedules || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load schedules');
    } finally {
      setLoading(false);
    }
  }, [open, streamId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      if (jobKind === 'heartbeat') {
        await apiClient.createAiStreamSchedule(streamId, {
          schedule: newCron,
          workflowName: 'ai-heartbeat',
        });
      } else {
        const maxN = Math.min(20, Math.max(1, parseInt(collectMax, 10) || 5));
        await apiClient.createAiStreamSchedule(streamId, {
          schedule: newCron,
          workflowName: 'ai-scheduled-collect',
          parameters: {
            _aiJobKind: 'collect_jira',
            _aiRoleCode: roleCode?.trim() || '',
            collectJqlB64: utf8ToBase64(collectJql.trim() || 'updated >= -1d ORDER BY updated DESC'),
            collectMaxResults: String(maxN),
          },
        });
      }
      setShowCreate(false);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to create schedule');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (schedule: AiSchedule) => {
    try {
      await apiClient.updateAiStreamSchedule(streamId, schedule.id, { enabled: !schedule.enabled });
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to update schedule');
    }
  };

  const handleDelete = async (schedule: AiSchedule) => {
    try {
      await apiClient.deleteAiStreamSchedule(streamId, schedule.id);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to delete schedule');
    }
  };

  const scheduleSubtitle =
    roleCode && roleLabel
      ? `${streamDisplayName} · ${roleLabel} (${roleCode})`
      : streamDisplayName;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent maxWidth="lg" className="max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            <span className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-violet-600 dark:text-violet-400 shrink-0" />
              <span>Scheduled jobs: {scheduleSubtitle}</span>
            </span>
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-3 min-h-0">
          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <p>
              <strong className="text-gray-800 dark:text-gray-200">Stream health</strong> (<code className="text-xs">ai-heartbeat</code>)
              calls portal internal APIs for active runs, activity, and costs only — it does not read email, Teams, Jira, or ADO, and does not ingest work.
            </p>
            <p>
              <strong className="text-gray-800 dark:text-gray-200">Jira collect &amp; ingest</strong> (<code className="text-xs">ai-scheduled-collect</code>)
              runs JQL against Jira using stream secrets and <code className="text-xs">jiraBaseUrl</code> from org defaults, then creates triaged workflow runs per issue.
              Requires the <code className="text-xs">ai-scheduled-collect</code> WorkflowTemplate in the Argo namespace.
            </p>
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading schedules...
            </div>
          ) : schedules.length === 0 && !showCreate ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
              No scheduled jobs for this stream.
            </div>
          ) : (
            <div className="space-y-2">
              {schedules.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-3 py-2"
                >
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-mono text-gray-900 dark:text-gray-100">{s.schedule}</code>
                      <span className="text-xs text-gray-500 dark:text-gray-400">({humanizeCron(s.schedule)})</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <Badge variant={s.enabled ? 'success' : 'warning'} className="text-[10px]">
                        {s.enabled ? 'Active' : 'Paused'}
                      </Badge>
                      <span className="font-mono">{s.workflowName}</span>
                      {s.parameters?._aiRoleCode ? (
                        <span>role: {s.parameters._aiRoleCode}</span>
                      ) : null}
                      {s.lastRun && <span>Last run: {new Date(s.lastRun).toLocaleString()}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      title={s.enabled ? 'Pause' : 'Enable'}
                      onClick={() => void handleToggle(s)}
                    >
                      {s.enabled ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                      title="Delete"
                      onClick={() => void handleDelete(s)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showCreate && (
            <div className="space-y-3 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 p-3">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">New scheduled job</p>
              <div className="flex flex-col gap-2">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Job type</span>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setJobKind('heartbeat')}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      jobKind === 'heartbeat'
                        ? 'bg-violet-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    Stream health (ai-heartbeat)
                  </button>
                  <button
                    type="button"
                    onClick={() => setJobKind('collect_jira')}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      jobKind === 'collect_jira'
                        ? 'bg-violet-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    Jira collect &amp; ingest
                  </button>
                </div>
              </div>
              {jobKind === 'collect_jira' && (
                <div className="space-y-2">
                  <label className="text-xs text-gray-500 dark:text-gray-400">JQL</label>
                  <Input
                    value={collectJql}
                    onChange={(e) => setCollectJql(e.target.value)}
                    className="font-mono text-sm"
                  />
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 dark:text-gray-400 shrink-0">Max issues</label>
                    <Input
                      type="number"
                      min={1}
                      max={20}
                      value={collectMax}
                      onChange={(e) => setCollectMax(e.target.value)}
                      className="w-20 font-mono text-sm"
                    />
                  </div>
                  {roleCode ? (
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Triage context: role <span className="font-mono">{roleCode}</span>
                    </p>
                  ) : (
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      Open scheduled jobs from a role row to attach a role code, or the first role/leaf on the stream is used.
                    </p>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((p) => (
                  <button
                    key={p.cron}
                    type="button"
                    onClick={() => setNewCron(p.cron)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      newCron === p.cron
                        ? 'bg-violet-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={newCron}
                  onChange={(e) => setNewCron(e.target.value)}
                  placeholder="Cron expression"
                  className="font-mono text-sm flex-1"
                />
                <Button size="sm" onClick={() => void handleCreate()} disabled={creating || !newCron.trim()}>
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Create
                </Button>
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          {!showCreate && (
            <Button variant="outline" size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="w-4 h-4 mr-1" /> Add schedule
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
